import { CUSTOMER_STATUS } from '@monorepo/shared/schemas/common'
import { useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { useInfiniteCustomers } from '../hooks/useInfiniteCustomers'
import type { CustomerResponse } from '../services/customerService'
import { CustomerDrawer } from './CustomerDrawer'
import { CustomerFilters } from './CustomerFilters'
import { CustomersTable } from './CustomersTable'

interface CustomerListFilters {
  page?: number
  limit?: number
  field?: string
  operator?: string
  value?: string
  status?: string
}

export function CustomerList() {
  const navigate = useNavigate()
  const [uiFilters, setUiFilters] = useState<CustomerListFilters>({
    page: 1,
    limit: 50,
  })
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Map UI filters to API format (server-side filtering)
  const apiFilters = useMemo(
    () => ({
      page: uiFilters.page || 1,
      limit: uiFilters.limit || 50,
      filter_field: uiFilters.field,
      filter_operator: uiFilters.operator,
      filter_value: uiFilters.value,
      status: uiFilters.status,
    }),
    [uiFilters]
  )

  // Use React Query for data fetching with API filters
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteCustomers(apiFilters)

  // Flatten paginated data (already filtered by server)
  const allCustomers = useMemo(() => {
    return data?.pages.flatMap(page => page.data) || []
  }, [data])

  const handleFiltersChange = useCallback((newFilters: CustomerListFilters) => {
    setUiFilters(newFilters)
  }, [])

  const handleReset = useCallback(() => {
    setUiFilters({
      page: 1,
      limit: 50,
    })
  }, [])

  const handleCreateCustomer = () => {
    setDrawerOpen(true)
  }

  const handleEditCustomer = (customer: CustomerResponse) => {
    navigate({ to: `/customers/${customer.id}` })
  }

  const handleRowClick = (customer: CustomerResponse) => {
    navigate({ to: `/customers/${customer.id}` })
  }

  // Server-side filtering is already applied - use allCustomers directly
  const totalCustomers = allCustomers.length
  const activeCustomers = allCustomers.filter(
    c => c.status === CUSTOMER_STATUS.active
  ).length

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <PageTitle
            title='Customers'
            subtitle='Manage your customers and their accounts'
          />
          <div className='flex items-center gap-2 mt-3'>
            <Badge variant='secondary' className='text-sm'>
              {totalCustomers} Total
            </Badge>
            <Badge variant='default' className='text-sm'>
              {activeCustomers} Active
            </Badge>
          </div>
        </div>
        <Button onClick={handleCreateCustomer} className='gap-2'>
          <Plus className='w-4 h-4' />
          Create Customer
        </Button>
      </div>

      {/* Filters */}
      <CustomerFilters
        filters={uiFilters}
        onFiltersChange={handleFiltersChange}
        onReset={handleReset}
      />

      {/* Table */}
      <CustomersTable
        customers={allCustomers}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        onEditCustomer={handleEditCustomer}
        onRowClick={handleRowClick}
      />

      {/* Customer Drawer for Create */}
      <CustomerDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  )
}
